import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { CloudDownload, Print } from '@material-ui/icons';
import { KeyboardDatePicker } from '@material-ui/pickers';
import AdminTemplate from 'components/templates/AdminTemplate';
import { format } from 'date-fns';
import MaterialTable, { QueryResult, Query, Column } from 'material-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { URL_DISPATCHES_REPORTS } from 'constants/url';
import useUser from 'hooks/useUser';
import XLSX from 'xlsx';

interface DispatchesReportsData {
  Fecha: string;
  envio: string;
  pedido: string;
  nombre: string;
  cant: string;
  sku: string;
  guia: string;
}

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    padding: `0 ${theme.spacing(2)}px`,
    paddingBottom: theme.spacing(2)
  }
}));

const DispatchesReports: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableRef = useRef<any>();

  // States
  const [tableColumns, setTableColumns] = useState<Column<DispatchesReportsData>[]>([]);
  const [isTableLoading, setTableLoading] = useState<boolean>(false);

  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [lastQueryURL, setLastQueryURL] = useState<string>('');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startDateError, setStartDateError] = useState<string | undefined>(undefined);

  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endDateError, setEndDateError] = useState<string | undefined>(undefined);

  const [orderID, setOrderID] = useState<string>('');
  const [sku, setSku] = useState<string>('');
  const [delivery, setDelivery] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState<string>('');

  // Hooks
  const classes = useStyles();
  const { jwt } = useUser();

  // Handlers
  async function handleQueryChange(query: Query<DispatchesReportsData>): Promise<QueryResult<DispatchesReportsData>> {
    setTableLoading(true);

    const filters: { column: 'date' | 'order_id' | 'sku' | 'shipping' | 'trakingNumber'; value: string[] }[] = [];

    if (startDate && endDate) {
      filters.push({ column: 'date', value: [startDate.toISOString(), endDate.toISOString()] });
    }

    if (orderID.length > 0) {
      filters.push({ column: 'order_id', value: [orderID] });
    }

    if (sku.length > 0) {
      filters.push({ column: 'sku', value: [sku] });
    }

    if (delivery.length > 0) {
      filters.push({ column: 'shipping', value: [delivery] });
    }

    if (trackingNumber.length > 0) {
      filters.push({ column: 'trakingNumber', value: [trackingNumber] });
    }

    if (filters.length > 0) {
      let url =
        URL_DISPATCHES_REPORTS + `?filters=${JSON.stringify(filters)}&offset=${query.page + 1}&limit=${query.pageSize}`;

      if (query.orderBy && typeof query.orderBy.field === 'string' && query.orderBy.field.length > 0) {
        url += `&orderBy=${query.orderBy.field}`;

        if (query.orderDirection) {
          url += `&order_direction=${query.orderDirection.toUpperCase()}`;
        }
      }

      const result = await axios.get<{ data: DispatchesReportsData[]; total: number }>(url, {
        headers: { Authorization: `Bearer ${jwt}` }
      });

      setLastQueryURL(url);
      setTableLoading(false);

      return {
        data: result.data.data,
        page: query.page,
        totalCount: Number(result.data.total) || 0
      };
    }

    setLastQueryURL('');
    setTableLoading(false);

    return {
      data: [],
      page: query.page,
      totalCount: 0
    };
  }

  function handleConsultClick(): void {
    if (startDate && !endDate) {
      setEndDateError('Debes ingresar una fecha fin');
      return;
    } else if (!startDate && endDate) {
      setStartDateError('Debes ingresar una fecha de inicio');
      return;
    }

    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }

  const handlePrint = useCallback(async () => {
    if (lastQueryURL.length === 0) {
      return;
    }

    setPrintLoading(true);
    const result = await axios.get<{ data: string; extension: string }>(lastQueryURL + '&download=true', {
      headers: { Authorization: `Bearer ${jwt}` }
    });

    const book = XLSX.read(result.data.data, { type: 'base64' });

    const sheets = book.SheetNames;
    const htmlData = XLSX.utils.sheet_to_html(book.Sheets[sheets[0]], {
      header: '<title>Reporte de despachos</title>'
    });

    const printWindow = window.open(
      '',
      'Reporte de despachos',
      'dependent=yes,locationbar=no,scrollbars=yes,menubar=yes,resizable,screenX=50,screenY=50,width=850,height=1050'
    );

    if (printWindow) {
      printWindow.document.documentElement.innerHTML = htmlData;
      printWindow.print();
    }

    setPrintLoading(false);
  }, [lastQueryURL, jwt]);

  const handleDownload = useCallback(async () => {
    if (lastQueryURL.length === 0) {
      return;
    }

    setDownloadLoading(true);
    const result = await axios.get<{ data: string; extension: string }>(lastQueryURL + '&download=true', {
      headers: { Authorization: `Bearer ${jwt}` }
    });

    const downloadElement = document.createElement('a');

    downloadElement.href = `data:text/plain;base64,${result.data.data}`;
    downloadElement.download = `Reporte de despachos.${result.data.extension}`;

    document.body.appendChild(downloadElement);

    downloadElement.click();
    downloadElement.remove();
    setDownloadLoading(false);
  }, [lastQueryURL, jwt]);

  // Effects
  useEffect(() => {
    if (tableColumns.length === 0) {
      setTableColumns([
        {
          field: 'Fecha',
          render({ Fecha }) {
            return format(new Date(Fecha), 'dd/MM/yyyy');
          },
          title: 'Fecha del pedido'
        },
        { field: 'pedido', title: 'Pedido' },
        { field: 'sku', title: 'SKU' },
        { field: 'nombre', title: 'Nombre del producto' },
        { field: 'cant', title: 'Cantidad de productos a despachar' },
        { field: 'envio', title: 'Tipo de envío' },
        { field: 'guia', title: 'Guía' }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminTemplate>
      <Grid container spacing={2} justify="center">
        <Grid item xs={12} style={{ marginBottom: 10 }}>
          <Typography variant="h5" component="h1" align="center">
            Reporte de despachos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper className={classes.filtersContainer}>
            <Grid container spacing={1} justify="center">
              <Grid item xs={12} md={2}>
                <KeyboardDatePicker
                  disableFuture
                  error={Boolean(startDateError)}
                  format="yyyy-MM-dd"
                  helperText={startDateError}
                  inputVariant="outlined"
                  label="Fecha de inicio"
                  margin="dense"
                  onChange={(value) => {
                    setStartDate(value);
                    setStartDateError(undefined);
                  }}
                  value={startDate}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <KeyboardDatePicker
                  disableFuture
                  error={Boolean(endDateError)}
                  format="yyyy-MM-dd"
                  helperText={endDateError}
                  inputVariant="outlined"
                  label="Fecha fin"
                  margin="dense"
                  minDate={startDate ?? undefined}
                  minDateMessage="La fecha fin debe ser mayor a la fecha de inicio"
                  onChange={(value) => {
                    setEndDate(value);
                    setEndDateError(undefined);
                  }}
                  value={endDate}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  disabled={isTableLoading}
                  label="Pedido"
                  margin="dense"
                  onChange={(e) => setOrderID(e.target.value)}
                  value={orderID}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  disabled={isTableLoading}
                  label="SKU"
                  margin="dense"
                  onChange={(e) => setSku(e.target.value)}
                  value={sku}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel shrink>Tipo de envío</InputLabel>
                  <Select
                    displayEmpty
                    label="Tipo de envío"
                    onChange={(e) => setDelivery(e.target.value as string)}
                    placeholder="Selecciona un tipo de envío"
                    value={delivery}
                  >
                    <MenuItem value="">Selecciona un tipo de envío</MenuItem>
                    <MenuItem value="propio">Propio</MenuItem>
                    <MenuItem value="coordinadora">Coordinadora</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  disabled={isTableLoading}
                  label="Guía"
                  margin="dense"
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  value={trackingNumber}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth color="primary" onClick={handleConsultClick} variant="contained">
                  BUSCAR
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <MaterialTable
            actions={[
              {
                disabled: printLoading,
                icon: () => <Print />,
                isFreeAction: true,
                onClick: handlePrint,
                tooltip: 'Imprimir'
              },
              {
                disabled: downloadLoading,
                icon: () => <CloudDownload />,
                isFreeAction: true,
                onClick: handleDownload,
                tooltip: 'Descargar'
              }
            ]}
            columns={tableColumns}
            data={handleQueryChange}
            isLoading={isTableLoading}
            options={{
              search: false,
              pageSize: 10,
              pageSizeOptions: [10, 20, 100]
            }}
            tableRef={tableRef}
            title="Reporte de despachos"
          />
        </Grid>
      </Grid>
    </AdminTemplate>
  );
};

export default DispatchesReports;
